<template>
  <div :style="cssVariablesBinding" data-x="html" data-x-toggle="html-overflow-hidden">
    <Theme9BaseTheHeader />

    <div v-if="shouldShowComponent" id="msgboxxWidget" />

    <main>
      <BreadCrumbMenu v-if="$route.path !== '/'" theme="theme9" />
      <slot />
    </main>
    <div class="progress-wrap tw-z-[9999]">
      <svg class="progress-circle svg-content" width="100%" height="100%" viewBox="-1 -1 102 102">
        <path d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98" />
      </svg>
    </div>

    <Theme9BaseTheFooter />
  </div>
</template>

<script lang="ts">
import { mapState } from 'pinia';
import IndexGlobal from '~/mixins/index.global';
import LayoutsMixin from '~/mixins/layouts.mixin';
import { useWebsiteStore } from '~/units/website/store';
import BreadCrumbMenu from '~/components/common/BreadCrumbMenu.vue';
import AvailableComponentsForTheme9 from '~/components/theme9/AvailableComponentsForTheme9.vue';
import { useAppMode } from '~/composables';

export default defineNuxtComponent({
  name: 'Theme9',

  mixins: [IndexGlobal, LayoutsMixin],

  components: { AvailableComponentsForTheme9, BreadCrumbMenu },

  data() {
    return {
      containers: [
        {
          name: 'BLOGS_VIEW',
          classes: 'blog-wrap5',
        },
        {
          name: 'BLOG_DETAIL_VIEW',
          classes: 'blog-wrap6',
        },
      ],
    };
  },

  head(context) {
    const appMode = useAppMode();

    if (appMode.server && context.ssrContext) {
      const websiteStore = useWebsiteStore();
      // const isRoderickThomas = websiteStore.companyKey === 'roderickthomas';
      const isRochfordStokes = false;

      const tagScripts = (() => {
        if (process.env.APP_STAGE === 'production') {
          return [
            ...websiteStore.googleTagsScripts,
            {
              key: 'company-analytics',
              type: 'text/javascript',
              src: `/trackers/${websiteStore.company?.company_key}.js`,
              async: false,
            },
          ];
        }
        return [];
      })();

      return {
        link: [
          {
            rel: 'stylesheet',
            href: `/theme9/css/theme/bootstrap.min.css`,
            type: 'text/css',
          },
          {
            rel: 'stylesheet',
            href: `/theme9/css/theme/line-awesome.min.css`,
            type: 'text/css',
          },
          {
            rel: 'stylesheet',
            href: `/theme9/css/theme/fontAwesomePro.css`,
            type: 'text/css',
          },
          {
            rel: 'stylesheet',
            href: `/theme9/css/theme/animate.css`,
            type: 'text/css',
          },
          {
            rel: 'stylesheet',
            href: `/theme9/css/theme/barfiller.css`,
            type: 'text/css',
          },
          {
            rel: 'stylesheet',
            href: `/theme9/css/theme/magnific-popup.css`,
            type: 'text/css',
          },
          {
            rel: 'stylesheet',
            href: `/theme9/css/theme/flaticon.css`,
            type: 'text/css',
          },
          {
            rel: 'stylesheet',
            href: `/theme9/css/theme/owl.carousel.css`,
            type: 'text/css',
          },
          {
            rel: 'stylesheet',
            href: `/theme9/css/theme/slick.css`,
            type: 'text/css',
          },
          {
            rel: 'stylesheet',
            href: `/theme9/css/theme/nice-select.css`,
            type: 'text/css',
          },
          {
            rel: 'stylesheet',
            href: `/theme9/css/theme/backToTop.css`,
            type: 'text/css',
          },
          {
            rel: 'stylesheet',
            href: `/theme9/css/theme/metismenu.css`,
            type: 'text/css',
          },
          {
            rel: 'stylesheet',
            href: `/theme9/css/theme/odometer.min.css`,
            type: 'text/css',
          },
          {
            rel: 'stylesheet',
            href: `/theme9/css/theme/style.css`,
            type: 'text/css',
          },
          {
            rel: 'stylesheet',
            href: `/theme9/css/${websiteStore.company?.company_key}/index.css`,
            type: 'text/css',
            fetchPriority: 'low',
          },

          isRochfordStokes
            ? {
                rel: 'stylesheet',
                href: 'https://cdn.msgboxx.io/static/css/bundle.min.css',
                type: 'text/css',
              }
            : null,
        ],
        script: [
          ...tagScripts,
          isRochfordStokes
            ? {
                src: 'https://cdn.msgboxx.io/static/js/bundle.min.js',
                async: true,
                onload: "window.msgboxxClient='ROCHFORDSTOKES';",
                body: true,
              }
            : null,
          isRochfordStokes
            ? {
                src: `https://assets.calendly.com/assets/external/widget.js`,
                type: 'text/javascript',
                async: true,
              }
            : null,
          {
            src: `/theme9/js/vendor/jquery-3.6.0.min.js`,
            type: 'text/javascript',
            async: false,
          },
          // company js in the header
          {
            src: `/theme9/js/${websiteStore.company?.company_key}/index.js`,
            type: 'text/javascript',
            async: false,
          },
          // Scripts at the end of body
          {
            src: `/theme9/js/vendor/popper.min.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/bootstrap.min.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/wow.min.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/pure-counter.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/owl.carousel.min.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/slick.min.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/magnific-popup.min.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/isotope-3.0.6-min.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/jquery.nice-select.min.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/backToTop.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/metismenu.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/jquery.barfiller.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/jquery.appear.min.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/odometer.min.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/gsap.min.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/gsap-scroll-to-plugin.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/SplitText.min.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/ScrollSmoother.min.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/ScrollTrigger.min.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/smoother-script.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/heading-animation.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/paragraph-animation.js`,
            type: 'text/javascript',
            body: true,
          },
          {
            src: `/theme9/js/vendor/main.js`,
            type: 'text/javascript',
            body: true,
          },
        ],
      };
    }
  },

  computed: {
    ...mapState(useWebsiteStore, ['currentPage', 'company']),

    cssVariablesBinding() {
      return {
        ...this.cssVariables,
        '--c-primary': this.colors.primary || '#E4002B',

        '--akya-text-color': 'var(--c-text_primary)',
        '--akya-background-color': 'var(--c-section_primary)',
        '--akya-confirm-button-text-color': 'var(--c-text_secondary)',
        '--akya-confirm-button-background-color': 'var(--c-primary)',
      };
    },

    isRoderickThomas() {
      /* return useWebsiteStore().companyKey === 'copeandco'; */
      return false;
    },

    shouldShowComponent() {
      const currentPath = this.$route.path;
      const isInHeadsUpAlerts = currentPath.startsWith('/heads-up-alerts');
      return this.isRochfordStokes && !isInHeadsUpAlerts;
    },
  },
});
</script>
